import React from "react";
import { SURVEY_QUESTION_TYPE } from "utils/graphql/Global";
import { BooleanQuestion } from "../boolean-question/boolean-question";
import { CommentOnlyQuestion } from "../comment-only-question/comment-only-question";
import { DateFieldQuestion } from "../date-fielld-question/date-field-question";
import { DropdownQuestion } from "../dropdown-question/dropdown-question";
import { MCQQuestion } from "../mcq-question/mcq-question";
import { MCQWithCommentQuestion } from "../mcq-with-comment-question/mcq-with-comment-question";
import { MultiSelectQuestion } from "../multi-select-question/multi-select-question";
import { TextBoxQuestion } from "../textbox-question/textbox-question";

interface QuestionData {
  questionId: string;
  htmlText: string;
  sequence: number;
  questionType: string | null;
  isMandatory: boolean;
  questionHint?: string | null;
  options: Array<{
    optionId: string;
    htmlText: string;
    sequence: number;
    isSelected: boolean | null;
  }>;
}

interface SurveyContainerProps {
  question: QuestionData;
  questionIndex: number;
  answer: any;
  onAnswerChange: (questionId: string, value: any) => void;
  validationErrors?: {
    showMandatoryError?: boolean;
    showInvalidInputError?: boolean;
    showMinCharError?: boolean;
    showMaxCharError?: boolean;
    customInvalidInputMessage?: string;
  };
  minCharacters?: number;
  maxCharacters?: number;
}

export const SurveyContainer: React.FC<SurveyContainerProps> = ({
  question,
  questionIndex,
  answer,
  onAnswerChange,
  validationErrors = {},
  minCharacters,
  maxCharacters,
}) => {
  const handleMCQChange = (questionId: string, optionId: string, optionText: string) => {
    onAnswerChange(questionId, { optionId, optionText });
  };

  const handleMCQWithCommentChange = (questionId: string, optionId: string) => {
    onAnswerChange(questionId, { ...answer, optionId });
  };

  const handleCommentChange = (comment: string) => {
    onAnswerChange(question.questionId, { ...answer, comment });
  };

  const handleTextBoxChange = (questionId: string, value: string) => {
    onAnswerChange(questionId, { value });
  };

  const handleDropdownChange = (questionId: string, optionId: string) => {
    onAnswerChange(questionId, { optionId });
  };

  const handleBooleanChange = (questionId: string, value: string) => {
    onAnswerChange(questionId, { value });
  };

  const handleMultiSelectChange = (questionId: string, optionId: string, checked: boolean) => {
    const currentValues = answer?.selectedValues || [];
    const newValues = checked
      ? [...currentValues, optionId]
      : currentValues.filter((id: string) => id !== optionId);
    onAnswerChange(questionId, { selectedValues: newValues });
  };

  const handleDateChange = (questionId: string, value: string, invalidDate = false) => {
    onAnswerChange(questionId, { value, invalidDate });
  };

  switch (question.questionType) {
    case SURVEY_QUESTION_TYPE.ONLYMCQ:
      return (
        <MCQQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          options={question.options}
          isMandatory={question.isMandatory}
          showMandatoryError={validationErrors.showMandatoryError || false}
          onChange={handleMCQChange}
        />
      );

    case SURVEY_QUESTION_TYPE.MCQWithComment:
      return (
        <MCQWithCommentQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          options={question.options}
          isMandatory={question.isMandatory}
          questionIndex={questionIndex}
          commentValue={answer?.comment || ""}
          showMandatoryError={validationErrors.showMandatoryError || false}
          onChange={handleMCQWithCommentChange}
          onCommentChange={handleCommentChange}
        />
      );

    case SURVEY_QUESTION_TYPE.ONLYComment:
      return (
        <CommentOnlyQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          isMandatory={question.isMandatory}
          questionHint={question.questionHint}
          commentValue={answer?.comment || ""}
          showMandatoryError={validationErrors.showMandatoryError || false}
          showMinCharError={validationErrors.showMinCharError || false}
          showMaxCharError={validationErrors.showMaxCharError || false}
          minCharacters={minCharacters}
          maxCharacters={maxCharacters}
          onCommentChange={handleCommentChange}
        />
      );

    case SURVEY_QUESTION_TYPE.TextBox:
    case SURVEY_QUESTION_TYPE.Numeric:
      return (
        <TextBoxQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          isMandatory={question.isMandatory}
          questionHint={question.questionHint}
          isNumeric={question.questionType == SURVEY_QUESTION_TYPE.Numeric ? true : false}
          value={answer?.value || ""}
          hasError={validationErrors.showMandatoryError || false}
          onChange={handleTextBoxChange}
        />
      );

    case SURVEY_QUESTION_TYPE.Dropdown:
      return (
        <DropdownQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          options={question.options}
          isMandatory={question.isMandatory}
          questionHint={question.questionHint}
          value={answer?.optionId || ""}
          hasError={validationErrors.showMandatoryError || false}
          onChange={handleDropdownChange}
        />
      );

    case SURVEY_QUESTION_TYPE.Boolean:
      return (
        <BooleanQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          questionHint={question.questionHint}
          isMandatory={question.isMandatory}
          value={answer?.value || ""}
          showMandatoryError={validationErrors.showMandatoryError || false}
          onChange={handleBooleanChange}
        />
      );

    case SURVEY_QUESTION_TYPE.MultiSelect:
      return (
        <MultiSelectQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          options={question.options}
          isMandatory={question.isMandatory}
          selectedValues={answer?.selectedValues || []}
          showMandatoryError={validationErrors.showMandatoryError || false}
          onChange={handleMultiSelectChange}
        />
      );

    case SURVEY_QUESTION_TYPE.DateField:
      return (
        <DateFieldQuestion
          questionId={question.questionId}
          questionText={question.htmlText}
          questionHint={question.questionHint}
          isMandatory={question.isMandatory}
          value={answer?.value || ""}
          hasError={validationErrors.showMandatoryError || false}
          showInvalidInputError={validationErrors.showInvalidInputError || false}
          customInvalidInputMessage={validationErrors.customInvalidInputMessage}
          onChange={handleDateChange}
        />
      );

    default:
      return <div>Unsupported question type</div>;
  }
};
